import { ParticipantKind, Track } from 'livekit-client'
import type { Room } from 'livekit-client'

import { getScreenShareTrackPublishOptions } from '@/src/lib/screenShareOptions'

/**
 * Протокол жалоб на кодек экрана и аварийного перехода на VP8.
 *
 * История: в октябре 2026 чёрный экран у зрителей давал аппаратный H.264-кодер
 * публикатора, а `backupCodec` SDK не спасал — он срабатывает по возможностям SDP,
 * а не по факту декодирования. Поэтому зритель сам детектит «кадры идут, разбора
 * нет» и просит публикатора переиздать тот же трек в VP8 через data channel.
 */

/** Топик data channel для всех сообщений протокола. */
export const SCREEN_SHARE_CODEC_TOPIC = 'screen-share-codec'

/** Жалоба зрителя: поток идёт, а декодер молчит. */
export type ScreenShareCodecComplaint = {
  type: 'SCREEN_SHARE_CODEC_COMPLAINT'
  trackSid: string
  publisherIdentity: string
  framesReceived: number
  framesDecoded: number
  keyFramesDecoded: number
}

/**
 * Предупреждение публикатора: сейчас будет пересоздание публикации.
 *
 * Зрители по нему замораживают автопин, чтобы раскладка не прыгнула
 * в сетку на время пересогласования.
 */
export type ScreenShareCodecSwitching = {
  type: 'SCREEN_SHARE_CODEC_SWITCHING'
  oldTrackSid: string
  publisherIdentity: string
}

/** Итог перехода: новый sid, кодеки и число жалоб, которые его вызвали. */
export type ScreenShareCodecSwitched = {
  type: 'SCREEN_SHARE_CODEC_SWITCHED'
  oldTrackSid: string
  newTrackSid: string | null
  publisherIdentity: string
  codecFrom: string
  codecTo: string
  complaintsCount: number
  ok: boolean
}

/** Любое сообщение протокола. */
export type ScreenShareCodecMessage =
  | ScreenShareCodecComplaint
  | ScreenShareCodecSwitching
  | ScreenShareCodecSwitched

/** Минимальный срез счётчиков получателя, по которому решается жалоба. */
export type ScreenShareReceiverCounters = {
  packetsReceived: number
  framesReceived: number
  framesDecoded: number
  keyFramesDecoded: number
  pliCount: number
}

/** Состояние зрителя, при котором сэмпл считается честным. */
export type ScreenShareSampleContext = {
  tabVisible: boolean
  subscribed: boolean
  muted: boolean
  isVp8: boolean
}

/** Участник в объёме, нужном для отличия агента от живого зрителя. */
export type AgentLikeParticipant = {
  identity?: string
  kind?: unknown
  permissions?: { agent?: boolean } | null
  isAgent?: boolean
}

/** Моменты сэмплов после подписки: решение о жалобе падает на ~3,2 с. */
export const CODEC_WATCHDOG_SAMPLE_DELAYS_MS = [1000, 2000, 3000]

/** Пауза между жалобами одного зрителя по одному треку. */
export const CODEC_COMPLAINT_COOLDOWN_MS = 30_000

/** Сколько жалоб шлёт один зритель по одному треку, дальше он замолкает навсегда. */
export const MAX_CODEC_COMPLAINTS_PER_TRACK = 2

/**
 * Сколько плохих дельт подряд нужно для жалобы: решение падает на ~3,2 с.
 *
 * На второй секунде ключевой кадр ещё может опаздывать, поэтому первая плохая
 * дельта жалобу не вызывает — только серия из двух подряд.
 */
export const COMPLAINT_STREAK_TO_SEND = 2

/** Размер комнаты, с которого для перехода нужны уже две жалобы. */
export const CODEC_SWITCH_VIEWER_THRESHOLD = 6

/** Окно агрегации жалоб у публикатора. */
export const CODEC_SWITCH_COMPLAINT_WINDOW_MS = 2000

/** Пауза между переходами и лимит попыток на одну демонстрацию. */
export const CODEC_SWITCH_COOLDOWN_MS = 30_000
export const MAX_CODEC_SWITCHES_PER_TRACK = 2

/** Сколько зрители держат замороженный автопин на время перехода. */
export const CODEC_SWITCH_PIN_SUPPRESS_MS = 6000

const pinSuppressUntilByPublisher = new Map<string, number>()

/**
 * Проверяет, что значение — обычный объект для разбора сообщения.
 *
 * @param value - результат JSON.parse полезной нагрузки
 */
function isMessageRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Кодирует сообщение протокола для отправки через data channel.
 *
 * @param message - жалоба, предупреждение о переходе или его итог
 */
export function encodeScreenShareCodecMessage(message: ScreenShareCodecMessage): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(message))
}

/**
 * Разбирает входящее сообщение протокола, мусор отбрасывает молча.
 *
 * Строгая проверка полей нужна, потому что топик слушают все участники
 * и чужой JSON с совпадением по топику не должен ронять вотчдоги.
 *
 * @param payload - сырая полезная нагрузка из RoomEvent.DataReceived
 */
export function decodeScreenShareCodecMessage(payload: Uint8Array): ScreenShareCodecMessage | null {
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(payload))

    if (!isMessageRecord(parsed) || typeof parsed.type !== 'string') {
      return null
    }

    if (parsed.type === 'SCREEN_SHARE_CODEC_COMPLAINT') {
      if (
        typeof parsed.trackSid !== 'string' ||
        typeof parsed.publisherIdentity !== 'string'
      ) {
        return null
      }

      return {
        type: 'SCREEN_SHARE_CODEC_COMPLAINT',
        trackSid: parsed.trackSid,
        publisherIdentity: parsed.publisherIdentity,
        framesReceived: typeof parsed.framesReceived === 'number' ? parsed.framesReceived : 0,
        framesDecoded: typeof parsed.framesDecoded === 'number' ? parsed.framesDecoded : 0,
        keyFramesDecoded: typeof parsed.keyFramesDecoded === 'number' ? parsed.keyFramesDecoded : 0,
      }
    }

    if (parsed.type === 'SCREEN_SHARE_CODEC_SWITCHING') {
      if (
        typeof parsed.oldTrackSid !== 'string' ||
        typeof parsed.publisherIdentity !== 'string'
      ) {
        return null
      }

      return {
        type: 'SCREEN_SHARE_CODEC_SWITCHING',
        oldTrackSid: parsed.oldTrackSid,
        publisherIdentity: parsed.publisherIdentity,
      }
    }

    if (parsed.type === 'SCREEN_SHARE_CODEC_SWITCHED') {
      if (
        typeof parsed.oldTrackSid !== 'string' ||
        typeof parsed.publisherIdentity !== 'string'
      ) {
        return null
      }

      return {
        type: 'SCREEN_SHARE_CODEC_SWITCHED',
        oldTrackSid: parsed.oldTrackSid,
        newTrackSid: typeof parsed.newTrackSid === 'string' ? parsed.newTrackSid : null,
        publisherIdentity: parsed.publisherIdentity,
        codecFrom: typeof parsed.codecFrom === 'string' ? parsed.codecFrom : 'h264',
        codecTo: typeof parsed.codecTo === 'string' ? parsed.codecTo : 'vp8',
        complaintsCount: typeof parsed.complaintsCount === 'number' ? parsed.complaintsCount : 0,
        ok: parsed.ok === true,
      }
    }

    return null
  } catch {
    return null
  }
}

/**
 * Решает по паре сэмплов, просить ли публикатора сменить кодек.
 *
 * Счётчики нарастающие, поэтому смотрит только дельту: пакеты и кадры растут,
 * а декодер молчит. `freezeCount` сюда не входит осознанно — без отрендеренных
 * кадров фризы не фиксируются и в нашем чёрном экране он остаётся нулевым.
 *
 * @param prev - предыдущий сэмпл получателя
 * @param curr - свежий сэмпл получателя
 */
export function shouldComplainAboutCodec(
  prev: ScreenShareReceiverCounters,
  curr: ScreenShareReceiverCounters,
): boolean {
  const packetsGrow = curr.packetsReceived > prev.packetsReceived
  const framesGrow = curr.framesReceived > prev.framesReceived
  const nothingDecoded = curr.framesDecoded === 0 && curr.keyFramesDecoded === 0

  // Основная сигнатура: поток идёт, а декодер не разобрал даже ключевой кадр.
  if (packetsGrow && framesGrow && nothingDecoded) {
    return true
  }

  const noFramesYet = curr.packetsReceived > 0 && curr.framesReceived === 0
  const pliGrows = curr.pliCount > prev.pliCount

  // Ранний маркер: кадров нет вообще, зритель уже просит ключевые кадры.
  if (noFramesYet && pliGrows && nothingDecoded) {
    return true
  }

  return false
}

/**
 * Ведёт серию плохих дельт: хорошая обнуляет, плохая наращивает.
 *
 * Серия вынесена в чистую функцию, чтобы правило «жалоба только со второй
 * плохой дельты подряд» проверялось юнит-тестом, а не только стендом.
 *
 * @param prevStreak - длина текущей серии плохих дельт
 * @param bad - свежая дельта признана отказом декодера
 */
export function updateComplaintStreak(prevStreak: number, bad: boolean): number {
  if (!bad) {
    return 0
  }

  return prevStreak + 1
}

/**
 * Проверяет, можно ли доверять сэмплу.
 *
 * Скрытая вкладка и пауза adaptiveStream дают нули декодирования при живом
 * потоке, а жаловаться на уже включённый VP8 бессмысленно — это не поломка.
 *
 * @param context - видимость вкладки, подписка, мьют и кодек трека
 */
export function canSampleForCodecWatchdog(context: ScreenShareSampleContext): boolean {
  return context.tabVisible && context.subscribed && !context.muted && !context.isVp8
}

/**
 * Проверяет лимит жалоб зрителя: не чаще паузы и не больше лимита на трек.
 *
 * @param sentCount - сколько жалоб уже ушло по этому треку
 * @param lastSentAtMs - когда ушла последняя, null если ещё ни одной
 * @param nowMs - текущее время в миллисекундах
 */
export function complaintAllowed(
  sentCount: number,
  lastSentAtMs: number | null,
  nowMs: number,
): boolean {
  if (sentCount >= MAX_CODEC_COMPLAINTS_PER_TRACK) {
    return false
  }

  if (lastSentAtMs !== null && nowMs - lastSentAtMs < CODEC_COMPLAINT_COOLDOWN_MS) {
    return false
  }

  return true
}

/**
 * Считает, сколько жалоб нужно для перехода.
 *
 * Маленькая комната (до 5 зрителей) чинится по первой жалобе, большая —
 * только по двум от разных зрителей: цена ложного перехода там выше.
 *
 * @param viewerCount - число живых зрителей трека без агентов
 */
export function requiredComplaints(viewerCount: number): number {
  return viewerCount >= CODEC_SWITCH_VIEWER_THRESHOLD ? 2 : 1
}

/**
 * Отличает агента от живого зрителя при подсчёте комнаты.
 *
 * Egress и голосовые агенты в число жалобщиков не входят: жаловаться
 * они не умеют, а порог считается по тем, кто реально смотрит экран.
 * Признаки повторяют `isFedorAgentParticipant`, но без имён аватаров —
 * здесь важен только факт «это не живой зритель».
 *
 * @param participant - участник комнаты
 */
export function isAgentLikeParticipant(participant: AgentLikeParticipant | null | undefined): boolean {
  if (!participant) {
    return false
  }

  if (
    participant.kind === ParticipantKind.AGENT ||
    participant.isAgent === true ||
    participant.permissions?.agent === true
  ) {
    return true
  }

  const identity = participant.identity ?? ''

  return identity.startsWith('agent-')
}

/**
 * Считает живых зрителей трека без агентов.
 *
 * @param room - текущая комната livekit
 * @param trackSid - sid публикации экрана
 */
export function countLiveScreenShareViewers(room: Room, trackSid: string): number {
  let count = 0

  room.remoteParticipants.forEach((participant) => {
    if (isAgentLikeParticipant(participant)) {
      return
    }

    participant.trackPublications.forEach((publication) => {
      if (
        publication.trackSid === trackSid &&
        publication.source === Track.Source.ScreenShare &&
        publication.isSubscribed
      ) {
        count += 1
      }
    })
  })

  return count
}

/**
 * Проверяет, что трек уже остановлен и издавать его бессмысленно.
 *
 * Отдельная функция, а не сравнение в месте вызова: TypeScript сужает тип
 * `readyState` после первой проверки до `live` и вторую ругается, а трек
 * между проверками реально может остановиться.
 *
 * @param track - видеотрек демонстрации экрана
 */
function isMediaTrackEnded(track: MediaStreamTrack): boolean {
  return track.readyState === 'ended'
}

/**
 * Тихо переиздаёт тот же видеотрек экрана в VP8 без диалога выбора окна.
 *
 * Трек не останавливается: `stopOnUnpublish` передан явно, потому что
 * поведение по умолчанию зависит от опций комнаты. Аудио экрана не трогаем —
 * это отдельная публикация. Состояние кнопки в `useTrackToggle` не меняется:
 * поток в `screenStreamRef` остаётся тем же, меняется только публикация.
 *
 * @param room - текущая комната livekit, где мы — публикатор экрана
 */
export async function republishScreenShareAsVp8(
  room: Room,
): Promise<{ oldTrackSid: string; newTrackSid: string } | null> {
  const publication = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)
  const mediaTrack = publication?.track?.mediaStreamTrack

  if (!publication || !mediaTrack || isMediaTrackEnded(mediaTrack)) {
    return null
  }

  const oldTrackSid = publication.trackSid

  await room.localParticipant.unpublishTrack(mediaTrack, false)

  // Пользователь мог остановить показ в эти миллисекунды — мёртвое не издаём.
  if (isMediaTrackEnded(mediaTrack)) {
    return null
  }

  const options = getScreenShareTrackPublishOptions(room.options.publishDefaults, {
    source: Track.Source.ScreenShare,
    videoCodec: 'vp8',
    backupCodec: false,
  })
  const newPublication = await room.localParticipant.publishTrack(mediaTrack, options)

  return { oldTrackSid, newTrackSid: newPublication.trackSid }
}

/**
 * Запоминает начало перехода, чтобы зрители заморозили автопин.
 *
 * Вызывает и публикатор перед снятием публикации, и зрители по сообщению
 * `SWITCHING`: порядок data-пакета и переподписки не гарантирован.
 *
 * @param publisherIdentity - идентичность автора демонстрации
 */
export function noteCodecSwitchStarting(publisherIdentity: string): void {
  pinSuppressUntilByPublisher.set(publisherIdentity, Date.now() + CODEC_SWITCH_PIN_SUPPRESS_MS)
}

/**
 * Снимает заморозку автопина после итога перехода.
 *
 * @param publisherIdentity - идентичность автора демонстрации
 */
export function noteCodecSwitchSettled(publisherIdentity: string): void {
  pinSuppressUntilByPublisher.delete(publisherIdentity)
}

/**
 * Проверяет, заморожен ли автопин на время перехода.
 *
 * @param publisherIdentity - идентичность автора демонстрации
 * @param nowMs - текущее время, по умолчанию сейчас
 */
export function isCodecSwitchPinSuppressed(publisherIdentity: string, nowMs = Date.now()): boolean {
  const until = pinSuppressUntilByPublisher.get(publisherIdentity)

  if (until === undefined) {
    return false
  }

  if (nowMs >= until) {
    pinSuppressUntilByPublisher.delete(publisherIdentity)

    return false
  }

  return true
}
